import React, { useState } from 'react';
import { FaLock, FaKey, FaPlane, FaExclamationCircle, FaShieldAlt, FaCheck } from 'react-icons/fa';
import { changePassword } from '../api/client';

const ChangePassword = ({ onPasswordChanged }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Password validation checks
  const passwordChecks = {
    minLength: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    digit: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*()_+\-=\[\]{}|;:'",.<>?/`~\\]/.test(newPassword),
  };
  const allPasswordChecksPass = Object.values(passwordChecks).every(Boolean);

  const validateForm = () => {
    const newErrors = {};

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (!allPasswordChecksPass) {
      newErrors.newPassword = 'Password does not meet all requirements';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await changePassword(newPassword, confirmPassword);
      onPasswordChanged();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password. Please try again.';
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md animate-scale-in">
      <div className="bg-white rounded-2xl shadow-elevated p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center items-center gap-2 mb-4">
            <FaShieldAlt className="text-4xl text-primary-500" />
            <FaPlane className="text-3xl text-secondary-400" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-500 to-secondary-400 bg-clip-text text-transparent mb-2">
            Change Password
          </h1>
          <p className="text-neutral-600">Please set a new password to continue</p>
        </div>

        {/* Security Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <FaKey className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-amber-800 text-sm font-medium">Password Reset Required</p>
              <p className="text-amber-700 text-sm mt-1">
                You logged in with a temporary password. For security, please create a new password now.
              </p>
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {errorMessage && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-6 animate-slide-down flex items-start gap-3">
            <FaExclamationCircle className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm">{errorMessage}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* New Password Field */}
          <div>
            <label htmlFor="newPassword" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
              <FaLock className="text-primary-500" />
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min. 8 characters"
              className={`w-full px-4 py-3 rounded-lg border-2 transition-all ${
                errors.newPassword
                  ? 'border-red-500 bg-red-50'
                  : 'border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100'
              } outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
              disabled={loading}
            />
            {errors.newPassword && (
              <p className="text-red-600 text-sm mt-1">{errors.newPassword}</p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
              <FaLock className="text-primary-500" />
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              className={`w-full px-4 py-3 rounded-lg border-2 transition-all ${
                errors.confirmPassword
                  ? 'border-red-500 bg-red-50'
                  : 'border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100'
              } outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
              disabled={loading}
            />
            {errors.confirmPassword && (
              <p className="text-red-600 text-sm mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-primary text-white py-3 px-6 rounded-lg font-semibold text-lg shadow-travel hover:shadow-elevated hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Updating Password...
              </span>
            ) : (
              'Update Password'
            )}
          </button>
        </form>

        {/* Password Requirements */}
        <div className="mt-6 p-4 bg-neutral-50 rounded-lg">
          <p className="text-sm font-medium text-neutral-700 mb-2">Password Requirements:</p>
          <ul className="text-sm text-neutral-600 space-y-1">
            {[
              { key: 'minLength', label: 'At least 8 characters' },
              { key: 'uppercase', label: 'One uppercase letter (A-Z)' },
              { key: 'lowercase', label: 'One lowercase letter (a-z)' },
              { key: 'digit', label: 'One digit (0-9)' },
              { key: 'special', label: 'One special character (!@#$...)' },
            ].map(({ key, label }) => (
              <li key={key} className={`flex items-center gap-2 ${passwordChecks[key] ? 'text-green-600' : ''}`}>
                <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  passwordChecks[key] ? 'bg-green-500' : 'bg-neutral-300'
                }`}>
                  {passwordChecks[key] && <FaCheck className="text-white text-[8px]" />}
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Decorative Quote */}
      <p className="text-center text-white/80 mt-6 italic text-sm">
        "Security is not a product, but a process."
      </p>
    </div>
  );
};

export default ChangePassword;
